import type { EmailAddress, EmailBodyPart, EmailMessage } from "../core/shell-types";

type Email = EmailMessage;

export function senderLabel(email: Email): string {
  const first = email.from?.[0];
  if (!first) {
    return "(unknown sender)";
  }
  return first.name?.trim() || first.email;
}

export function addressLabel(address: EmailAddress): string {
  return address.name?.trim() ? `${address.name.trim()} <${address.email}>` : address.email;
}

export function addressListLabel(addresses: EmailAddress[] | null | undefined): string {
  return (addresses ?? []).map(addressLabel).join(", ");
}

export function receivedLabel(receivedAt: string): string {
  const date = new Date(receivedAt);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function fullReceivedLabel(receivedAt: string): string {
  return new Date(receivedAt).toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function isUnread(email: Email): boolean {
  return email.keywords.$seen !== true;
}

export function isStarred(email: Email): boolean {
  return email.keywords.$flagged === true;
}

// First readable body value for the given part list (textBody or htmlBody).
// EmailBodyPart.partId is always present on the Swift side; bodyValues is
// absent until the thread has been hydrated.
export function bodyText(email: Email, parts: EmailBodyPart[] | null | undefined): string | null {
  for (const part of flattenParts(parts ?? [])) {
    const value = email.bodyValues?.[part.partId];
    if (value) {
      return value.value + (value.isTruncated ? "\n[message truncated]" : "");
    }
  }
  return null;
}

function flattenParts(parts: EmailBodyPart[]): EmailBodyPart[] {
  return parts.flatMap((part) => [part, ...flattenParts(part.subParts ?? [])]);
}

export function senderInitials(email: Email): string {
  const first = email.from?.[0];
  const name = first?.name?.trim();
  if (name) {
    const words = name.split(/\s+/).filter(Boolean);
    return ((words[0]?.[0] ?? "") + (words.length > 1 ? (words[words.length - 1][0] ?? "") : ""))
      .toUpperCase()
      .slice(0, 2);
  }
  return (first?.email?.[0] ?? "?").toUpperCase();
}

// Deterministic per-sender hue so avatars are stable across renders/sessions.
export function senderHue(email: Email): number {
  const key = email.from?.[0]?.email ?? "";
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

export function parseAddressList(raw: string): EmailAddress[] {
  return raw
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(.*)<([^<>@\s]+@[^<>\s]+)>$/);
      if (match) {
        const name = match[1].trim().replace(/^"|"$/g, "");
        return name ? { name, email: match[2] } : { email: match[2] };
      }
      return { email: part };
    });
}
